import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Container, Paper, Typography, Button, Snackbar, Alert } from '@mui/material';
import PaymentsRounded from '@mui/icons-material/PaymentsRounded';
import CreditCardRounded from '@mui/icons-material/CreditCardRounded';
import PrintRounded from '@mui/icons-material/PrintRounded';
import { useOrder } from '../../state/OrderContext';
import { useMenu } from '../../state/MenuContext';
import { PRINT_SERVER } from '../../data/printServer';
import { formatPrice } from '../../utils/format';
import type { PaymentMethod } from '../../shared/types';

interface BillScreenProps {
  tableNumber: number;
  onPaid: () => void;
}

const BillScreen: React.FC<BillScreenProps> = ({ tableNumber, onPaid }) => {
  const { t, i18n } = useTranslation();
  const { state, payTable } = useOrder();
  const { getMenuItem } = useMenu();
  const [printing, setPrinting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const order = state.tables[tableNumber];
  const items = order?.items ?? [];
  const total = items.reduce((sum, line) => {
    const menuItem = getMenuItem(line.menuItemId);
    return sum + (menuItem ? menuItem.price * line.quantity : 0);
  }, 0);

  const handlePay = (method: PaymentMethod): void => {
    const methodLabel = method === 'cash' ? t('bill.methodCash') : t('bill.methodCard');
    payTable(tableNumber, method);
    window.alert(t('bill.paymentConfirmed', { num: tableNumber, method: methodLabel }));
    onPaid();
  };

  const handlePrintReceipt = async (): Promise<void> => {
    if (items.length === 0) return;
    setPrinting(true);
    const printItems = items.map((line) => {
      const menuItem = getMenuItem(line.menuItemId);
      return {
        name: menuItem?.name ?? line.menuItemId,
        quantity: line.quantity,
        price: menuItem?.price ?? 0,
      };
    });
    try {
      const r = await fetch(`${PRINT_SERVER}/print/receipt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tableNumber, items: printItems, total }),
      });
      const json = await r.json();
      if (!r.ok) throw new Error(json.error || 'Print failed');
      setSnackbar({ open: true, message: t('bill.receiptPrinted'), severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, message: t('bill.printFailed', { message: e.message }), severity: 'error' });
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 2 }}>
      <Paper elevation={0} sx={{ overflow: 'hidden', mb: 2, border: '1px solid #F5F5F5' }}>
        <Box sx={{ px: 2, pt: 1.75, pb: 1.25, borderBottom: '1px solid #F5F5F5' }}>
          <Typography sx={{ fontWeight: 500, fontSize: '0.7rem', color: '#9E9E9E', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            {t('bill.order')}
          </Typography>
        </Box>
        {items.map((line) => {
          const menuItem = getMenuItem(line.menuItemId);
          if (!menuItem) return null;
          return (
            <Box
              key={line.menuItemId}
              sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #F9F9F9' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box sx={{ backgroundColor: '#FFECEC', color: 'primary.main', borderRadius: '8px', fontWeight: 700, fontSize: '0.75rem', px: 1.125, py: 0.25 }}>
                  {line.quantity}×
                </Box>
                <Typography sx={{ fontSize: '0.9rem', color: '#424242' }}>{menuItem.name}</Typography>
              </Box>
              <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#212121' }}>
                {formatPrice(menuItem.price * line.quantity, i18n.language)}
              </Typography>
            </Box>
          );
        })}
      </Paper>

      <Paper
        elevation={0}
        sx={{ backgroundColor: 'primary.main', p: 2.25, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Typography sx={{ fontWeight: 500, fontSize: '1rem', color: 'rgba(255,255,255,0.85)' }}>{t('bill.total')}</Typography>
        <Typography sx={{ fontWeight: 800, fontSize: '1.75rem', color: '#fff' }}>{formatPrice(total, i18n.language)}</Typography>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mb: 1.25 }}>
        <Button
          variant="contained"
          onClick={() => handlePay('cash')}
          startIcon={<PaymentsRounded />}
          sx={{ backgroundColor: '#2E7D32', py: 1.75, flexDirection: 'column', gap: 0.5 }}
        >
          {t('bill.cash')}
        </Button>
        <Button
          variant="contained"
          onClick={() => handlePay('card')}
          startIcon={<CreditCardRounded />}
          sx={{ backgroundColor: '#1565C0', py: 1.75, flexDirection: 'column', gap: 0.5 }}
        >
          {t('bill.card')}
        </Button>
      </Box>

      <Button
        fullWidth
        variant="outlined"
        onClick={handlePrintReceipt}
        disabled={printing || items.length === 0}
        startIcon={<PrintRounded />}
        sx={{ borderColor: '#E0E0E0', color: '#757575' }}
      >
        {printing ? t('bill.receiptPrinting') : t('bill.printReceipt')}
      </Button>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default BillScreen;
